import React, { useEffect, useState } from 'react';
import { Loader2, Save, PhoneCall, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import {
  adminGetCallSettings, adminPatchCallSettings, adminGetCallStats, adminGetRecentCalls,
} from '../../api/o2oCalls';

const NUM_FIELDS = [
  ['max_duration_minutes', 'Durata massima chiamata (minuti)'],
  ['min_duration_minutes', 'Durata minima fatturata (minuti)'],
  ['min_credits', 'Crediti minimi per chiamare'],
  ['ring_timeout_seconds', 'Timeout risposta (secondi)'],
  ['waitlist_max_users', 'Lista d\'attesa · max utenti'],
  ['waitlist_max_wait_minutes', 'Lista d\'attesa · attesa max (minuti)'],
];

const BOOL_FIELDS = [
  ['audio_enabled', 'Chiamate audio abilitate'],
  ['video_enabled', 'Videochiamate abilitate'],
  ['waitlist_enabled', 'Lista d\'attesa abilitata'],
];

export const AdminCallSettingsTab = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [topicsText, setTopicsText] = useState('');

  useEffect(() => {
    adminGetCallSettings().then((d) => {
      setForm(d);
      setTopicsText((d.topic_options || []).join('\n'));
    }).catch(() => setForm({}));
  }, []);

  if (!form) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      NUM_FIELDS.forEach(([k]) => { patch[k] = Number(form[k]); });
      BOOL_FIELDS.forEach(([k]) => { patch[k] = !!form[k]; });
      patch.topic_options = String(topicsText || '')
        .split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 50);
      const fresh = await adminPatchCallSettings(patch);
      setForm(fresh);
      setTopicsText((fresh.topic_options || []).join('\n'));
      toast({ title: 'Impostazioni Chiamate salvate' });
    } catch (e) {
      toast({
        title: 'Salvataggio fallito',
        description: e.response?.data?.detail?.split?.('|')?.pop?.() || '',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-o2o-call-settings">
      <div className="grid gap-4 sm:grid-cols-2">
        {NUM_FIELDS.map(([key, label]) => (
          <label key={key} className="space-y-1.5 text-sm">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <Input
              type="number"
              value={form[key] ?? ''}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`o2o-setting-${key}`}
            />
          </label>
        ))}
      </div>
      <div className="space-y-3 rounded-xl border border-border p-4">
        {BOOL_FIELDS.map(([key, label]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <Switch
              checked={!!form[key]}
              onCheckedChange={(v) => setForm({ ...form, [key]: v })}
              data-testid={`o2o-setting-${key}`}
            />
          </div>
        ))}
      </div>
      <div className="space-y-2 rounded-xl border border-border p-4">
        <p className="text-xs font-semibold text-muted-foreground">
          Argomenti disponibili per le Voci Pro (uno per riga, max 50)
        </p>
        <textarea
          value={topicsText}
          onChange={(e) => setTopicsText(e.target.value)}
          rows={8}
          className="w-full rounded-md border border-border bg-background p-2 text-sm"
          data-testid="o2o-setting-topic-options"
        />
      </div>
      <Button onClick={save} disabled={saving} data-testid="o2o-settings-save-btn">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Salva impostazioni
      </Button>
    </div>
  );
};

const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

export const AdminCallStatsTab = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [days, setDays] = useState(30);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    adminGetCallStats(days).then(setStats).catch(() => setStats({ creators: [] }));
    adminGetRecentCalls(30).then((d) => setRecent(d.items)).catch(() => setRecent([]));
  }, [days, tick]);

  if (!stats || recent === null) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-o2o-call-stats">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((d) => (
          <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)} className="h-7 px-3 text-xs">
            {d} giorni
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setTick((x) => x + 1)} className="h-7 px-2" data-testid="o2o-stats-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Voce Pro</th>
              <th className="px-3 py-2">Ricevute</th>
              <th className="px-3 py-2">Accettate</th>
              <th className="px-3 py-2">Rifiutate</th>
              <th className="px-3 py-2">Perse</th>
              <th className="px-3 py-2">% risposta</th>
              <th className="px-3 py-2">Squillo medio</th>
              <th className="px-3 py-2">Durata media</th>
              <th className="px-3 py-2">Crediti generati</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {stats.creators.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Nessuna chiamata nel periodo.</td></tr>
            )}
            {stats.creators.map((r) => (
              <tr key={r.creator_id} data-testid={`o2o-stats-row-${r.creator_id}`}>
                <td className="px-3 py-2 font-semibold">
                  @{r.username}
                  {r.flagged_low_answer_rate && (
                    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-bold text-red-500" data-testid="o2o-flag-low-answer">
                      <AlertTriangle className="h-2.5 w-2.5" /> BASSA RISPOSTA
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{r.calls_received}</td>
                <td className="px-3 py-2 tabular-nums text-emerald-600">{r.accepted}</td>
                <td className="px-3 py-2 tabular-nums text-red-500">{r.rejected}</td>
                <td className="px-3 py-2 tabular-nums text-amber-600">{r.missed_timeout}</td>
                <td className="px-3 py-2 tabular-nums">{pct(r.answer_rate)}</td>
                <td className="px-3 py-2 tabular-nums">{r.avg_ring_seconds != null ? `${r.avg_ring_seconds}s` : '—'}</td>
                <td className="px-3 py-2 tabular-nums">{r.avg_duration_seconds != null ? `${Math.round(r.avg_duration_seconds / 60)} min` : '—'}</td>
                <td className="px-3 py-2 font-semibold tabular-nums gold-text">{r.credits_generated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
          <PhoneCall className="h-4 w-4 gold-text" /> Ultime chiamate
        </h3>
        <ul className="divide-y divide-border rounded-xl border border-border text-xs" data-testid="o2o-recent-calls">
          {recent.length === 0 && <li className="px-3 py-4 text-center text-muted-foreground">Nessuna chiamata.</li>}
          {recent.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span>
                @{c.caller?.username} → @{c.creator?.username}
                <span className="ml-2 text-muted-foreground">{c.mode} · {c.rate_credits_per_min} cr/min</span>
              </span>
              <span className="flex items-center gap-3 tabular-nums">
                <span className={
                  c.state === 'COMPLETED' ? 'text-emerald-600'
                    : c.state === 'ACTIVE' ? 'text-blue-500'
                    : c.state === 'RINGING' ? 'text-amber-600'
                    : 'text-red-500'
                }>{c.state}</span>
                {c.billed_credits != null && <span className="gold-text">{c.billed_credits} cr</span>}
                <span className="text-muted-foreground">
                  {new Date(c.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
